import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Users, UserPlus, Heart, MapPin, Calendar, MessageCircle } from 'lucide-react';
import { tribalSocialAPI } from '../services/api';
import FollowButton from './FollowButton';
import PeacePipeMessage from './PeacePipeMessage';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const FollowList = ({ 
  userId = null, // If null, shows current user's data
  listType = 'followers', // 'followers' or 'following'
  showTitle = true,
  limit = 20
}) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const { user: currentUser } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const isCurrentUser = !userId || userId === currentUser?.id;
  const displayUserId = userId || currentUser?.id;

  useEffect(() => {
    if (displayUserId) {
      loadUsers(true);
    }
  }, [displayUserId, listType]);

  const loadUsers = async (reset = false) => {
    if (!displayUserId) return;
    
    try {
      setLoading(true);
      const currentOffset = reset ? 0 : offset;
      
      const response = listType === 'followers' 
        ? await tribalSocialAPI.getFollowers(displayUserId, limit, currentOffset)
        : await tribalSocialAPI.getFollowing(displayUserId, limit, currentOffset);
      
      const newUsers = response.users || [];
      
      if (reset) {
        setUsers(newUsers);
        setOffset(limit);
      } else {
        setUsers(prev => [...prev, ...newUsers]);
        setOffset(prev => prev + limit);
      }
      
      setTotalCount(response.total_count || 0);
      setHasMore(response.has_more || false);
      
    } catch (error) {
      console.error(`Error loading ${listType}:`, error);
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to load ${listType}`,
      });
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => {
    if (!loading && hasMore) {
      loadUsers(false);
    }
  };

  const getFollowTypeIcon = (followType) => {
    switch (followType) {
      case 'surname_match':
        return '🧬';
      case 'tribal_match':
        return '🏔️';
      case 'region_match':
        return '🗺️';
      case 'healing_interest':
        return '🌿';
      default:
        return '✨';
    }
  };

  const getFollowTypeLabel = (followType) => {
    switch (followType) {
      case 'surname_match':
        return 'Surname Connection';
      case 'tribal_match':
        return 'Tribal Connection';
      case 'region_match':
        return 'Regional Connection';
      case 'healing_interest':
        return 'Healing Interest';
      default:
        return 'General Follow';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const handleUserClick = (userId) => {
    navigate(`/public-profile/${userId}`);
  };

  const title = listType === 'followers' ? 'Followers' : 'Following';
  const emptyMessage = listType === 'followers' 
    ? 'No followers yet' 
    : 'Not following anyone yet';

  if (loading && users.length === 0) {
    return (
      <Card>
        {showTitle && (
          <CardHeader>
            <CardTitle className="flex items-center">
              <Users className="w-5 h-5 mr-2" />
              {title} ({totalCount})
            </CardTitle>
          </CardHeader>
        )}
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center space-x-3 animate-pulse">
                <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-32 mb-1"></div>
                  <div className="h-3 bg-gray-200 rounded w-24"></div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      {showTitle && (
        <CardHeader>
          <CardTitle className="flex items-center">
            <Users className="w-5 h-5 mr-2" />
            {title} ({totalCount})
          </CardTitle>
        </CardHeader>
      )}
      <CardContent>
        {users.length === 0 ? (
          <div className="text-center py-8">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {emptyMessage}
            </h3>
            <p className="text-gray-500 text-sm mb-4">
              {listType === 'followers' 
                ? 'Start engaging with the community to build your network'
                : 'Discover and follow community members to see their updates'}
            </p>
            {isCurrentUser && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/explore')}
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Find People to Follow
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {users.map((userItem) => (
                <div 
                  key={userItem.user_id} 
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-4 flex-1 min-w-0">
                    <Avatar 
                      className="w-12 h-12 cursor-pointer hover:ring-2 hover:ring-amber-300 transition-all"
                      onClick={() => handleUserClick(userItem.user_id)}
                    >
                      <AvatarImage src={userItem.profile_picture} />
                      <AvatarFallback>
                        {userItem.tribal_name ? userItem.tribal_name.charAt(0).toUpperCase() : '?'}
                      </AvatarFallback>
                    </Avatar>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <h4 
                          className="font-medium text-gray-900 hover:text-amber-600 cursor-pointer transition-colors truncate"
                          onClick={() => handleUserClick(userItem.user_id)}
                          title={userItem.tribal_name || 'Unknown User'}
                        >
                          {userItem.tribal_name || 'Unknown User'}
                        </h4>
                        {userItem.follow_type !== 'general' && (
                          <span className="text-lg flex-shrink-0" title={getFollowTypeLabel(userItem.follow_type)}>
                            {getFollowTypeIcon(userItem.follow_type)}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-3 mt-1 flex-wrap">
                        {userItem.region_flag && (
                          <div className="flex items-center text-xs text-gray-500">
                            <MapPin className="w-3 h-3 mr-1" />
                            <span className="truncate">
                              {typeof userItem.region_flag === 'string' 
                                ? userItem.region_flag 
                                : userItem.region_flag.name || userItem.region_flag.region || 'Unknown Region'}
                            </span>
                          </div>
                        )}
                        
                        {userItem.followed_at && (
                          <div className="flex items-center text-xs text-gray-500">
                            <Calendar className="w-3 h-3 mr-1" />
                            {formatDate(userItem.followed_at)}
                          </div>
                        )}
                        
                        {userItem.follow_type !== 'general' && (
                          <Badge variant="outline" className="text-xs">
                            {getFollowTypeLabel(userItem.follow_type)}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Show follow button and message button only if it's not the current user viewing their own list */}
                  {userItem.user_id !== currentUser?.id && (
                    <div className="flex space-x-2 ml-4 flex-shrink-0">
                      <FollowButton
                        userId={userItem.user_id}
                        size="sm"
                      />
                      <PeacePipeMessage
                        receiverId={userItem.user_id}
                        receiverName={userItem.tribal_name}
                        trigger={
                          <Button variant="outline" size="sm">
                            <MessageCircle className="w-4 h-4 mr-1" />
                            Message
                          </Button>
                        }
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {hasMore && (
              <div className="mt-6 text-center">
                <Button
                  variant="outline"
                  onClick={loadMore}
                  disabled={loading}
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                  ) : null}
                  {loading ? 'Loading...' : 'Load More'}
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default FollowList;